/* @flow */
import Vue from 'vue'
import axios from 'axios'
import normalize from 'jsonapi-normalizer'
import oPath from 'object-path'
import query from 'query-string'

import {REMOVE_SESSION} from './session'
import {
  computePaginateSortFilterParams,
  computedCollectionCount,
  nullifyBlankStrings,
} from '@/utils/api'
import {NEW_NOTIFICATION, REPLACE_NOTIFICATION} from './notifications'
import type {JsonApiEntity} from '@/store'

export const REPLACE_END_DEVICES = 'REPLACE_END_DEVICES'
export const ADD_END_DEVICES = 'ADD_END_DEVICES'
export const MODIFY_END_DEVICE = 'MODIFY_END_DEVICE'
export const REMOVE_END_DEVICE = 'REMOVE_END_DEVICE'
export const SET_END_DEVICES_TOTAL = 'SET_END_DEVICES_TOTAL'

export const FETCH_END_DEVICES = 'FETCH_END_DEVICES'
export const FETCH_APPLICATION_NETWORK_END_DEVICES =
  'FETCH_APPLICATION_NETWORK_END_DEVICES'
export const FETCH_DEVICE_GROUP_END_DEVICES = 'FETCH_DEVICE_GROUP_END_DEVICES'
export const FETCH_END_DEVICE = 'FETCH_END_DEVICE'
export const CREATE_END_DEVICE = 'CREATE_END_DEVICE'
export const UPDATE_END_DEVICE = 'UPDATE_END_DEVICE'
export const DELETE_END_DEVICE = 'DELETE_END_DEVICE'

const initialState = () => ({
  entities: {},
  statistics: {
    total: null,
  },
})

export default {
  state: initialState(),
  getters: {
    endDeviceDefaults: () => ({}),
    endDevices: ({entities = {}}: Object) =>
      //$FlowFixMe
      Object.keys(entities).map(key => entities[key]),
    // Returns an object with all end-devices, keyed by end-device EUI
    endDevicesKeyedByEui: (state: Object, getters: Object) => {
      const endDevices = {}
      getters.endDevices.forEach(device => {
        endDevices[device.eui] = device
      })
      return endDevices
    },
    endDeviceByEui: (state: Object, getters: Object) => (eui: string) =>
      getters.endDevices.find(device => device.eui === eui) || {},
    endDevicesByAppEui: (state: Object, getters: Object) => (appEui: string) => {
      const targetAppNetId = getters.applicationNetworkByEui(appEui).id
      return getters.endDevices.filter(
        device => device.application_network_id === targetAppNetId
      )
    },
  },
  mutations: {
    RESET_STATE(state: Object) {
      Object.assign(state, initialState())
    },
    [REMOVE_SESSION](state: Object) {
      state.entities = initialState().entities
    },
    [REPLACE_END_DEVICES](state: Object, {entities = {}}: Object) {
      state.entities = entities
    },
    [ADD_END_DEVICES](state: Object, payload: Object) {
      state.entities = {
        ...state.entities,
        ...payload.entities,
      }
    },
    [MODIFY_END_DEVICE](state: Object, payload: Object) {
      state.entities[payload.id] = {
        ...state.entities[payload.id],
        ...payload,
      }
    },
    [REMOVE_END_DEVICE](state: Object, payload: Object) {
      Vue.delete(state.entities, payload.id)
    },
    [SET_END_DEVICES_TOTAL](state: Object, payload: Object) {
      state.statistics.total = payload.total
    },
  },
  actions: {
    [FETCH_END_DEVICES]: async function(
      {commit, dispatch}: Object,
      payload: Object = {}
    ) {
      try {
        let pathstring = '/end_devices'
        if (payload.context && payload.context.orgId) {
          pathstring = '/organizations/' + payload.context.orgId + '/end_devices'
        }

        const {data} = await axios.get(pathstring, {
          params: {
            ...computePaginateSortFilterParams(payload),
            exclude_group: payload.excludeDeviceGroup
              ? payload.context.deviceGroupEui
              : null,
          },
        })

        const {pagination} = data.meta
        commit(SET_END_DEVICES_TOTAL, {total: pagination.collection_count})
        commit(REPLACE_END_DEVICES, {
          entities: normalize(data).entities.end_devices,
        })

        return {
          success: true,
          // Return fetched data as flat array
          items: data.data.map(item => item.attributes),
          collectionCount: computedCollectionCount({
            endRow: payload.endRow,
            filteredCount: pagination.filtered_count,
            collectionCount: pagination.collection_count,
          }),
        }
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load end-devices ',
          detail: err.message,
          color: 'error',
        })
      }
      return {success: false}
    },
    [FETCH_APPLICATION_NETWORK_END_DEVICES]: async function(
      {commit, dispatch}: Object,
      payload: Object
    ) {
      try {
        const {data} = await axios.get(
          `/application_networks/${payload.context.appEui}/end_devices`,
          {
            params: computePaginateSortFilterParams(payload),
          }
        )

        commit(ADD_END_DEVICES, {
          entities: normalize(data).entities.end_devices,
        })

        const {pagination} = data.meta
        return {
          success: true,
          // Return fetched data as flat array
          items: data.data.map(item => item.attributes),
          collectionCount: computedCollectionCount({
            endRow: payload.endRow,
            filteredCount: pagination.filtered_count,
            collectionCount: pagination.collection_count,
          }),
        }
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load end-devices ',
          detail: err.message,
          color: 'error',
        })
      }
      return {success: false}
    },
    [FETCH_DEVICE_GROUP_END_DEVICES]: async function(
      {commit, dispatch}: Object,
      payload: Object
    ) {
      try {
        const {data} = await axios.get(
          `/device_groups/${payload.context.deviceGroupEui}/end_devices`,
          {
            params: computePaginateSortFilterParams(payload),
          }
        )

        commit(ADD_END_DEVICES, {
          entities: normalize(data).entities.end_devices,
        })

        const {pagination} = data.meta
        return {
          success: true,
          // Return fetched data as flat array
          items: data.data.map(item => item.attributes),
          collectionCount: computedCollectionCount({
            endRow: payload.endRow,
            filteredCount: pagination.filtered_count,
            collectionCount: pagination.collection_count,
          }),
        }
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load end-devices ',
          detail: err.message,
          color: 'error',
        })
      }
      return {success: false}
    },
    [FETCH_END_DEVICE]: async function({commit, dispatch}: Object, payload: Object) {
      try {
        const {data} = await axios.get(`/end_devices/${payload.id}`)
        const {entities} = normalize(data)
        commit(ADD_END_DEVICES, {entities: entities.end_devices})
        return {success: true}
      } catch (err) {
        const status = oPath.get(err, 'response.status')
        if (status !== 404) {
          dispatch(NEW_NOTIFICATION, {
            text: 'Failed to load end-device ',
            detail: err.message,
            color: 'error',
          })
        }
        return {success: false, status}
      }
    },
    [CREATE_END_DEVICE]: async function(
      {commit, dispatch}: Object,
      payload: JsonApiEntity
    ) {
      try {
        const {data} = await axios.post('/end_devices', {
          data: {
            type: 'end_devices',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(ADD_END_DEVICES, {entities: entities.end_devices})
        if (!payload.suppressNotifications) {
          dispatch(NEW_NOTIFICATION, {
            text: 'Saved new end-device!',
            color: 'success',
          })
        }
        return data.data.attributes.eui
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to save end-device ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [UPDATE_END_DEVICE]: async function({commit, dispatch}: Object, payload: Object) {
      let notificationId
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: 'Saving...',
          color: 'secondary',
          undoable: true,
        })
      }
      nullifyBlankStrings(payload.attributes)
      try {
        const {data} = await axios.patch(`/end_devices/${payload.id}`, {
          data: {
            type: 'end_devices',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(MODIFY_END_DEVICE, entities.end_devices[payload.id])
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: 'Saved!',
            color: 'success',
            undoable: true,
          })
        }
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Failed to update end-device ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [DELETE_END_DEVICE]: async function({commit, dispatch}: Object, payload: Object) {
      let notificationId
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: `Deleting end-device...`,
          color: 'secondary',
        })
      }

      try {
        await axios.delete(
          `/end_devices/${payload.id}?${query.stringify(payload.options)}`
        )
        commit(REMOVE_END_DEVICE, payload)
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: `Deleted end-device!`,
            color: 'success',
          })
        }
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: `Failed to delete end-device `,
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
  },
}



// WEBPACK FOOTER //
// ./src/store/end_devices.js